import React from "react";
import { Clock3 } from "lucide-react";
import { Link } from "react-router";
import * as helpers from "../../../shared/lib/helpers";
import { toast } from "../../../shared/ui/toast";
import { ItemListRelativeTime } from "./ItemListRelativeTime";
import { getItemLowerLimit } from "../model/item-list-quick-edit";

export const getAlignClass = (align?: "start" | "center" | "end") => {
  switch (align) {
    case "start":
      return "text-start";
    case "center":
      return "text-center";
    case "end":
    default:
      return "text-end";
  }
};

export const getFloorPriceReachedBasis = (item: any): "price" | "discount" | "price_exposed" | null => {
  const exposedRange = item.adjust_price_exposed_range;
  const basis = exposedRange?.is_enabled
    ? "price_exposed"
    : item.adjust_strategy === "adjust_price" || item.adjust_strategy === "adjust_price_match_discount"
      ? "price"
      : "discount";
  const range = exposedRange?.is_enabled
    ? exposedRange
    : basis === "price" ? item.adjust_price_range : item.adjust_discount_range;
  if (!range?.is_enabled || !range.value_min) return null;
  let lowerLimit = range.value_min;
  if (range.value_min_type === "percent") {
    const calculated = lowerLimit * (1 + item.price / 100);
    lowerLimit = Math.round(calculated);
    if (calculated % 1 === 0.5 && lowerLimit % 2 !== 0) lowerLimit -= 1;
  }
  return lowerLimit > 0 && item[basis] === lowerLimit ? basis : null;
};

const renderHighlightedText = (text: string, searchTerm: string) => (
  helpers.highlightSearchTermSegments(text, searchTerm).map((segment, index) => (
    segment.highlighted
      ? <mark className="bg-warning" key={index}>{segment.text}</mark>
      : <React.Fragment key={index}>{segment.text}</React.Fragment>
  ))
);

const handleMissingShopUrlClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
  event.preventDefault();
  toast.error("상점 URL이 설정되지 않았습니다.");
};

type ComparePortal = "danawa" | "enuri" | "naver";

const comparePortalDetails: Record<ComparePortal, { label: string; title: string; color: string }> = {
  danawa: { label: "D", title: "다나와", color: "#6bca20" },
  enuri: { label: "E", title: "에누리", color: "#0092ff" },
  naver: { label: "N", title: "네이버", color: "#1ec800" },
};

const isComparePortal = (value: string): value is ComparePortal => (
  Object.prototype.hasOwnProperty.call(comparePortalDetails, value)
);

const getComparePortal = (compareSite: string, compareUrl: string): ComparePortal | null => {
  if (isComparePortal(compareSite)) return compareSite;
  if (!compareUrl) return null;

  try {
    const host = new URL(compareUrl).hostname.toLowerCase();
    return (Object.keys(comparePortalDetails) as ComparePortal[]).find(
      (portal) => host === `${portal}.com` || host.endsWith(`.${portal}.com`),
    ) ?? null;
  } catch {
    return null;
  }
};

const getItemShopUrl = (item: any): string | null | undefined => {
  switch (item.shop.market_site) {
    case "smartstore":
      return item.shop.url ? `${item.shop.url}/products/${item.shop_item_id}` : null;
    case "auction":
      return `https://itempage.auction.co.kr/DetailView.aspx?itemno=${item.shop_item_id}`;
    case "gmarket":
      return `https://item.gmarket.co.kr/Item?goodscode=${item.shop_item_id}`;
    case "eleventh":
      return `https://www.11st.co.kr/products/${item.shop_item_id}`;
    case "ssgdotcom":
      return `https://www.ssg.com/item/itemView.ssg?itemId=${item.shop_item_id}`;
    case "lotteon":
      return `https://www.lotteon.com/p/product/${item.shop_item_id}`;
    default:
      return undefined;
  }
};

const renderItemShopLink = (item: any, children: React.ReactNode) => {
  if (!item.shop) return children;
  const href = getItemShopUrl(item);
  if (href === undefined) return null;

  return (
    <a
      title={item.name}
      href={href || "#"}
      target={href ? "_blank" : undefined}
      rel={href ? "noopener noreferrer" : undefined}
      onClick={href ? undefined : handleMissingShopUrlClick}
    >
      {children}
    </a>
  );
};

export interface RenderColumnDataOptions {
  filterCompareSite: string;
  filterItemStatus?: string;
  filterFloorReached?: boolean;
  filterKeepDefaultPrice?: string;
  filterCompareRange?: string;
  filterAccumulatedError?: string;
  activeSearchKeyword: string;
  onOpenCompareItemsSnapshot: (item: any) => void;
  onEditItemPrice?: (item: any) => void;
  onEditItemDiscount?: (item: { id: string }) => void;
  onEditItemDefaultPrice?: (item: any) => void;
  onEditItemLowerLimit?: (item: any) => void;
  onEditItemTargetRank?: (item: any) => void;
  onOpenItemEdit?: (event: React.MouseEvent<HTMLAnchorElement>, item: any) => void;
  onToggleItemActive: (item: any) => void;
  onToggleItemKeepDefaultPrice: (item: any) => void;
}

export const renderColumnData = (
  colId: string,
  item: any,
  opts: RenderColumnDataOptions
): React.ReactNode => {
  const {
    filterCompareSite,
    filterItemStatus,
    filterFloorReached,
    filterKeepDefaultPrice,
    filterCompareRange,
    filterAccumulatedError,
    activeSearchKeyword,
    onOpenCompareItemsSnapshot,
    onEditItemPrice,
    onEditItemDiscount,
    onEditItemDefaultPrice,
    onEditItemLowerLimit,
    onEditItemTargetRank,
    onOpenItemEdit,
    onToggleItemActive,
    onToggleItemKeepDefaultPrice,
  } = opts;

  switch (colId) {
    case "col-market":
      if (!item.shop) return "-";
      return (
        <span title={item.shop?.name || ""}>
          {renderItemShopLink(item, <>
            {item.shop.market_site === "smartstore" && (
              <span
                className="badge ms-1"
                style={{ backgroundColor: "#1ec800", color: "white" }}
                title={item.shop?.name || "스마트스토어"}
              >
                S
              </span>
            )}
            {item.shop.market_site === "auction" && (
              <span
                className="badge ms-1"
                style={{ backgroundColor: "#dc3545", color: "white" }}
                title={item.shop?.name || "옥션"}
              >
                A
              </span>
            )}
            {item.shop.market_site === "gmarket" && (
              <span
                className="badge ms-1"
                style={{ backgroundColor: "#198754", color: "white" }}
                title={item.shop?.name || "지마켓"}
              >
                G
              </span>
            )}
            {item.shop.market_site === "eleventh" && (
              <span
                className="badge ms-1"
                style={{ backgroundColor: "#198754", color: "white", paddingInline: "4px" }}
                title={item.shop?.name || "11번가"}
              >
                11
              </span>
            )}
            {item.shop.market_site === "lotteon" && (
              <span
                className="badge ms-1 market-lotteon"
                title={item.shop?.name || "롯데ON"}
              >
                L
              </span>
            )}
            {item.shop.market_site === "ssgdotcom" && (
              <span
                className="badge ms-1 market-ssgdotcom"
                title={item.shop?.name || "SSG닷컴"}
              >
                S
              </span>
            )}
          </>)}
        </span>
      );
    case "col-item-code": {
      const itemSearchParams = new URLSearchParams();
      if (filterCompareSite) itemSearchParams.set("compare_site", filterCompareSite);
      if (filterItemStatus) itemSearchParams.set("status", filterItemStatus);
      if (filterFloorReached) itemSearchParams.set("floor_reached", "1");
      if (filterKeepDefaultPrice) itemSearchParams.set("keep_default_price", filterKeepDefaultPrice);
      if (filterCompareRange) itemSearchParams.set("compare_range", filterCompareRange);
      if (filterAccumulatedError) itemSearchParams.set("has_error", filterAccumulatedError);
      if (activeSearchKeyword.trim()) itemSearchParams.set("q", activeSearchKeyword.trim());
      const itemSearch = itemSearchParams.toString();
      return (
        <Link
          to={`/items/${item.id}${itemSearch ? `?${itemSearch}` : ""}`}
          onClick={(event) => onOpenItemEdit?.(event, item)}
        >
          <small>{item.shop_item_id}</small>
        </Link>
      );
    }
    case "col-item-name":
      return (
        <span className="item-list-product-name" title={item.name}>
          {renderItemShopLink(
            item,
            <span>{renderHighlightedText(item.name, activeSearchKeyword)}</span>,
          )}
        </span>
      );
    case "col-item-price-reset":
      return (
        <button type="button" className="btn btn-link item-list-price-button" onClick={() => onEditItemDefaultPrice?.(item)} title="복구기준가 수정">
          {helpers.formatNumber(item.price_default)}
          <span className="small">원</span>
        </button>
      );
    case "col-keep-default-price":
      return renderKeepDefaultPriceToggle(item, onToggleItemKeepDefaultPrice);
    case "col-item-price":
      return (
        <button type="button" className="btn btn-link item-list-price-button" onClick={() => onEditItemPrice?.(item)} title="판매가 수정">
          {helpers.formatNumber(item.price)}
          <span className="small">원</span>
        </button>
      );
    case "col-item-discount-amount":
      return (
        <button type="button" className="btn btn-link item-list-price-button" onClick={() => onEditItemDiscount?.(item)} title="할인액 수정">
          {helpers.formatNumber(item.discount)}
          <span className="small">원</span>
        </button>
      );
    case "col-item-discount-percent":
      return item.discount > 0 ? (
        <span style={{ fontSize: "0.75rem" }}>
          {((item.discount / item.price) * 100).toFixed(2) + "%"}
        </span>
      ) : (
        "-"
      );
    case "col-compare-site": {
      const portal = getComparePortal(item.compare_site, item.compare_url);
      const portalDetails = portal ? comparePortalDetails[portal] : null;
      const hasPortalColor = isComparePortal(item.compare_site);
      const isMyItemMissing = !(item.compare_site === "direct"
        ? item.my_direct_compare_item_id
        : item.my_compare_item_id);
      const baseTitle = item.compare_site === "direct"
        ? `직접비교${portalDetails ? ` · ${portalDetails.title}` : ""}`
        : portalDetails?.title ?? "비교사이트";
      const title = isMyItemMissing ? `${baseTitle} · 내 상품 미지정` : baseTitle;
      const badge = (
        <span
          className={`badge mx-0 compare-site-badge${hasPortalColor ? "" : " compare-site-badge-transparent"}${portalDetails ? "" : " compare-site-badge-empty"}${isMyItemMissing ? " compare-site-badge-my-item-missing" : ""}`}
          style={hasPortalColor && portalDetails ? { backgroundColor: portalDetails.color, color: "white" } : undefined}
          title={title}
          aria-label={title}
        >
          {portalDetails?.label}
        </span>
      );

      return item.compare_url
        ? <a href={item.compare_url} target="_blank" rel="noopener noreferrer">{badge}</a>
        : badge;
    }
    case "col-compare-rank":
      return (
        <>
          <button
            type="button"
            className="badge ms-1 border text-black me-1"
            style={{ background: "transparent", cursor: "pointer" }}
            title="경쟁상품 가격 스냅샷 보기"
            onClick={(e) => {
              e.stopPropagation();
              onOpenCompareItemsSnapshot(item);
            }}
          >
            {item.compare_range == "items_all" && `전체`}
            {item.compare_range == "items_same_market" && `마켓`}
            {item.compare_range == "items_specified" && `지정`}
          </button>
          <button type="button" className="btn btn-link item-list-price-button" onClick={() => onEditItemTargetRank?.(item)} title="기준순위 수정">
            {`${item.target_rank}위`}
          </button>
        </>
      );
    case "col-adjust-gap":
      return (
        <span>
          <span>
            {helpers.formatNumber(item.desired_gap)}
            <span className="small">원</span>
          </span>
          <span className="badge ms-1 border text-black">{item.gap_price_basis === "price" ? "판" : "노"}</span>
        </span>
      );
    case "col-adjust-lower-limit": {
      const { field, range } = getItemLowerLimit(item);

      return (
        <button type="button" className="btn btn-link item-list-price-button" onClick={() => onEditItemLowerLimit?.(item)} title="조정하한가 수정">
          {range.value_min == null ? "-" : (
            <>
              <span className={getFloorPriceReachedBasis(item) !== null ? "item-floor-price-value" : undefined}>
                {helpers.formatNumber(range.value_min)}
                <span className="small">{range.value_min_type === "percent" ? "%" : "원"}</span>
              </span>
              <span className="badge ms-1 border text-black">{field === "adjust_price_exposed_range" ? "노" : field === "adjust_price_range" ? "판" : "할"}</span>
            </>
          )}
        </button>
      );
    }
    case "col-adjust-strategy":
      return (
        <>
          {item.adjust_strategy === "adjust_price" && (
            <span>
              <span
                className="badge border text-black"
                style={{ minWidth: "44px" }}
              >
                판가
              </span>
            </span>
          )}
          {item.adjust_strategy === "adjust_discount" && (
            <span>
              <span
                className="badge border text-black"
                style={{ minWidth: "44px" }}
              >
                할인
              </span>
            </span>
          )}
          {item.adjust_strategy === "adjust_price_match_discount" && (
            <span>
              <span
                className="badge border text-black"
                style={{ minWidth: "44px" }}
              >
                판/할
              </span>
            </span>
          )}
          {item.adjust_strategy === "adjust_discount_match_price" && (
            <span>
              <span
                className="badge border text-black"
                style={{ minWidth: "44px" }}
              >
                할/판
              </span>
            </span>
          )}
        </>
      );
    case "col-exposed-price":
      return item.price_exposed ? (
        <span>
          {helpers.formatNumber(item.price_exposed)}
          <span className="small">원</span>
        </span>
      ) : (
        "-"
      );
    case "col-coupon-discount":
      return item.price_exposed &&
        item.price_exposed < item.price - item.discount ? (
        <span>
          {helpers.formatNumber(
            item.price - item.discount - item.price_exposed
          )}
          <span className="small">원</span>
        </span>
      ) : (
        "-"
      );
    case "col-coupon-percent":
      return item.price_exposed &&
        item.price_exposed < item.price - item.discount ? (
        <span style={{ fontSize: "0.75rem" }}>
          {(
            ((item.price - item.discount - item.price_exposed) / item.price) *
            100
          ).toFixed(2) + "%"}
        </span>
      ) : (
        "-"
      );
    case "col-ranked-price":
      return (
        <span>
          {helpers.formatNumber(item.target_price_exposed)}
          <span className="small">원</span>
        </span>
      );
    case "col-lowest-price":
      return (
        <span>
          {helpers.formatNumber(item.price_lowest)}
          <span className="small">원</span>
        </span>
      );
    case "col-price-change":
      return "-";
    case "col-error-count":
      return item.error_count;
    case "col-active-status":
      return (
        <button
          type="button"
          className={`badge item-active-status ${item.is_active ? "is-active" : "is-inactive"}`}
          onClick={(e) => {
            e.stopPropagation();
            void onToggleItemActive(item);
          }}
        >
          {item.is_active ? "활성" : "비활"}
        </button>
      );
    case "col-active-schedule-count":
      return (item.active_schedule_count ?? 0) > 0 ? (
        <Clock3 className="status-icon" aria-hidden="true" />
      ) : (
        "-"
      );
    case "col-processed-at":
      if (item.is_urgent_enqueue_processing) {
        return <small>{item.is_urgent_enqueue_pending ? "우선 처리중 · 재요청 대기" : "우선 처리중"}</small>;
      }
      if (item.is_urgent_enqueue_pending) {
        return <small>{item.is_processing ? "처리중 · " : ""}우선 처리 대기</small>;
      }
      if (item.is_processing) {
        return <small>처리중</small>;
      }
      return <small><ItemListRelativeTime timestamp={item.processed_at} /></small>;
    default:
      return null;
  }
};

export const renderKeepDefaultPriceToggle = (
  item: { keep_default_price?: boolean },
  onToggle: (item: { keep_default_price?: boolean }) => void,
): React.ReactNode => (
  <button
    type="button"
    className={`badge ms-1 me-1 item-active-status ${item.keep_default_price ? "is-active" : "is-inactive"}`}
    onClick={(e) => {
      e.stopPropagation();
      void onToggle(item);
    }}
  >
    복구
  </button>
);

export const renderColumnHeader = (
  colId: string,
  colConfig: any,
  resizeHandle?: React.ReactNode,
): React.ReactNode => {
  const headerLabel = colConfig.labelShort;
  const headerContent = headerLabel

  return (
    <th
      key={colId}
      scope="col"
      style={
        colConfig.maxWidth !== null
          ? {
              minWidth: colConfig.minWidth,
              width: colConfig.maxWidth,
              maxWidth: colConfig.maxWidth,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }
          : { minWidth: colConfig.minWidth }
      }
      className={`${colConfig.visible ? "mx-0 px-0" : "d-none"} text-center ${colId === "col-processed-at" ? "item-list-scroll-edge" : ""}`.trim()}
      title={colConfig.label}
      aria-label={colId === "col-active-schedule-count" ? colConfig.label : undefined}
    >
      {headerContent}
      {resizeHandle}
    </th>
  );
};
